import {DateTime, DurationUnits, ToISOTimeOptions, DateObjectUnits, DateTimeUnit, DateTimeJSOptions} from "luxon";
import {RRDuration} from "../RRDuration";
import type {Period} from "./Period";
import {monthNames, WEEKDAY_MAP} from "./RRDate.data";
import type {IDurationObject, Format, FormatOut, supportedLocaleString, WEEKDAY} from "./RRDate.types";

type DurationType = "hours" | "minutes" | "seconds" | "milliseconds";

export default class RRDate {
  private _date: DateTime;
  private _inputFormat: Format = "yyyy-MM-dd HH:mm:ss";
  private _inputTimezone = "Europe/Copenhagen";

  /**
   *
   * @param datestr - a datestring you wants to operate on
   * @param inputFormat - a format which we can extend - default yyyy-MM-dd HH:mm:ss
   * @param inputTimezone - IANA-specified zones are string identifiers like "America/New_York"
   * @param outputFormat - a format which we can extend - default = inputFormat
   */
  constructor(datestr?: string, inputFormat?: Format, inputTimezone?: string) {
    this._inputFormat = inputFormat ? inputFormat : this._inputFormat;
    this._inputTimezone = inputTimezone ? inputTimezone : this._inputTimezone;

    if (!datestr) {
      this._date = DateTime.local({zone: this._inputTimezone});
    } else if (inputFormat === "yyyy-MM-ddTHH:mm:ss") {
      this._date = DateTime.fromISO(datestr, {zone: this._inputTimezone});
    } else {
      this._date = DateTime.fromFormat(datestr, this._inputFormat, {zone: this._inputTimezone});
    }
  }

  public get isValid(): boolean {
    return this._date.isValid;
  }

  /**
   *
   * @param duration -- e.g. {minute: number}
   * @returns - our datetime string
   */
  public plus = (duration: IDurationObject): RRDate => {
    this._date = this._date.plus(duration);
    return this;
  };

  /**
   *
   * @param duration -- e.g. {minute: number}
   * @returns - our datetime string
   */
  public minus = (duration: IDurationObject): RRDate => {
    this._date = this._date.minus(duration);
    return this;
  };

  /**
   *
   * @param days -- e.g. 1 - accepts negative
   * @returns - RRDate with added days
   */
  public addDays = (days: number): RRDate => {
    this._date = this.plus({day: days})._date;
    return this;
  };

  /**
   *
   * @param hours -- e.g. 1 - accepts negative
   * @returns - RRDate with added hours
   */
  public addHours = (hours: number): RRDate => {
    this._date = this.plus({hour: hours})._date;
    return this;
  };

  /**
   *
   * @param minutes -- e.g. 1 - accepts negative
   * @returns - RRDate with added hours
   */
  public addMinutes = (minutes: number): RRDate => {
    this._date = this.plus({minute: minutes})._date;
    return this;
  };

  /**
   *
   * @param timezone - a timezone string of the desired output - e.g. Europe/Copenhagen
   * @returns - a DateTime modified to the desired timezone
   */
  public setZone = (timezone: string): RRDate => {
    this._date = this._date.setZone(timezone);
    return this;
  };

  /**
   *
   * @param values - set the values of specified units - e.g. {second: number}
   * @returns - a DateTime modified to the desired unit value
   */
  public set = (values: DateObjectUnits): RRDate => {
    this._date = this._date.set(values);
    return this;
  };

  /**
   *
   * @returns - the epoch milliseconds of this DateTime.
   *
   */
  public toMillis = (): number => this._date.toMillis();

  /**
   * Read [this](https://moment.github.io/luxon/#/formatting?id=table-of-tokens) for help on formatting
   * @param outputFormat - the output string format
   * @param locale - the language, e.g. "da".
   * @param ISOOutputOptions - ISO output options
   * @returns - a string in the desired format or defaults to input format
   */
  public toString = (outputFormat?: FormatOut, locale: string = "da", ISOOutputOptions?: ToISOTimeOptions): string => {
    const format = outputFormat ? outputFormat : this._inputFormat;

    if (format === "W") {
      return this._date.weekNumber.toString();
    }

    if (format === "ISO") {
      return this._date.toISO(ISOOutputOptions);
    }

    const formattedDate = this._date.toFormat(format, {locale});

    switch (outputFormat) {
      case "EEE":
        return formattedDate.substring(0, 3);
      case "d. dd/MM/yy kl. HH.mm": {
        const datePart = this._date.toFormat("dd/MM/yy", {locale});
        const timePart = this._date.toFormat("HH:mm", {locale});
        return `d. ${datePart} kl. ${timePart}`;
      }
      case "DD. MMM YYYY": {
        const datePart = this._date.toFormat("d. MMM");
        const yearPart = this._date.toFormat("yyyy-MM-dd HH:mm:ss").substring(0, 4);
        return `${datePart} ${yearPart}`;
      }
      default:
        return formattedDate;
    }
  };

  /**
   *
   * @param RRDate
   * @param RRDate
   * @param unit - e.g. "minutes"
   * @returns - and Duration object, with keys, like x.minutes
   */
  static diff(date1: RRDate, date2: RRDate, unit?: DurationUnits): RRDuration {
    return new RRDuration(date1._date.diff(date2._date, unit).toObject());
  }

  /**
   * if THIS is "before" / "the same" / "after" than OTHER
   * @param date2 other
   * @returns -1 = THIS is after OTHER; 0 = THIS is the same as OTHER; 1 = this id before OTHER
   */
  public compare(date2: RRDate): -1 | 1 | 0 {
    const thisMillis = this.toMillis();
    const date2Millis = date2.toMillis();
    return thisMillis > date2Millis ? -1 : thisMillis < date2Millis ? 1 : 0;
  }

  /**
   * @param date2 - if "this" comes before this
   * @returns true if "this" comes before date2
   */
  public isBefore(date2: RRDate): boolean {
    return this.compare(date2) > 0;
  }

  /**
   * @param date2 - if "this" comes after this
   * @returns true if "this" comes after date2
   */
  public isAfter(date2: RRDate): boolean {
    return this.compare(date2) < 0;
  }

  /**
   * @param date2 - if "this" comes before this or equal
   * @returns true if "this" comes before date2
   */
  public isBeforeOrEqual(date2: RRDate): boolean {
    return this.compare(date2) >= 0;
  }

  /**
   * @param date2 - if "this" comes after this or equal
   * @returns true if "this" comes after date2
   */
  public isAfterOrEqual(date2: RRDate): boolean {
    return this.compare(date2) <= 0;
  }

  /**
   * @returns 1 for monday 7 for sunday
   */
  public get weekday(): number {
    return this._date.weekday;
  }

  /**
   * @param weekday - to get as number
   * @returns 1 for monday, 2 tuesday ... 7 sunday
   */
  public static weekdayToNumber(weekday: WEEKDAY): number {
    return WEEKDAY_MAP[weekday];
  }

  /**
   * @param date2 - to compare this date against
   * @returns true if dates are equal
   */
  public equals(date2: RRDate): boolean {
    return RRDate.equals(this, date2);
  }

  /**
   * @param date1 - first date
   * @param date2 - second date
   * @returns true if date1 and date2 equals
   */
  public static equals(date1: RRDate, date2: RRDate): boolean {
    if (date1._inputFormat === date2._inputFormat) return date1.toMillis() === date2.toMillis();

    return this.diff(date1, date2).toString("S") === "0";
  }

  /**
   * @param date1 - first date
   * @param date2 - second date
   * @returns -1 if date1 is before date2, 1 if date1 is after date2, 0 if equal
   */
  public static compare(date1: RRDate, date2: RRDate): -1 | 1 | 0 {
    return date2.compare(date1);
  }

  public monthName(month: number, localeString: supportedLocaleString): string {
    return monthNames[localeString][month - 1];
  }

  /**
   * @param from and to datetimes to check if is within that period / overlaps
   * @returns true if "this" is within the period
   */
  public overlap({from, to}: {from: RRDate; to: RRDate} | Period): boolean {
    if (this.isBefore(from) && this.isBefore(to)) return false;
    if (this.isAfter(from) && this.isAfter(to)) return false;
    return true;
  }

  /**
   * @param unit - the unit to go to the beginning of. Can be 'year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second', or 'millisecond'
   * @returns a RRDate modified to the desired unit value
   */
  public startOf(unit: DateTimeUnit): RRDate {
    this._date = this._date.startOf(unit);
    return this;
  }

  /**
   * @param unit - the unit to go to the end of. Can be 'year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second', or 'millisecond'
   * @returns a RRDate modified to the desired unit value
   */
  public endOf(unit: DateTimeUnit): RRDate {
    this._date = this._date.endOf(unit);
    return this;
  }

  /**
   * Get the minute of the hour (0-59).
   *
   * @example
   * new RRDate("2017-05-25 09:30:11").minute //=> 30
   */
  get minute(): number {
    return this._date.minute;
  }

  public suppress(values: DurationType[]): RRDate {
    values.forEach((unit) => {
      if (unit === "milliseconds") this._date = this._date.minus({millisecond: this._date.millisecond});
      if (unit === "seconds") this._date = this._date.minus({second: this._date.second});
      if (unit === "minutes") this._date = this._date.minus({minute: this._date.minute});
      if (unit === "hours") this._date = this._date.minus({hour: this._date.hour});
    });
    return this;
  }

  /**
   * @returns returns a copy of this RRDate
   */
  public clone(): RRDate {
    return new RRDate(this.toString(this._inputFormat), this._inputFormat);
  }

  public static fromMillis(milliseconds: number, options?: DateTimeJSOptions): RRDate {
    const date = new RRDate();
    date._date = DateTime.fromMillis(milliseconds, {zone: date._inputTimezone, ...options});
    return date;
  }

  public lastDayOfMonth(): number {
    const year = this._date.year;
    const monthIndex = this._date.month - 1;
    const lastDate = new Date(year, monthIndex + 1, 0).getDate();
    return lastDate;
  }
}
